import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TAG_OPTIONS } from "@/data/forge";

const field =
  "w-full bg-white/[0.04] border border-white/12 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-ember transition-colors";

export default function NewPost() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [kind, setKind] = useState(params.get("kind") || "creation");
  const [form, setForm] = useState({
    title: "",
    body: "",
    creation_type: "dice",
    contents: "",
    duration: "30 days",
    difficulty: "Intermediate",
    feedback_type: "idea",
  });
  const [tags, setTags] = useState([]);
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) {
      toast.error("Please use an image under 1.5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        kind,
        title: form.title,
        body: form.body,
        tags: kind === "feedback" ? [] : tags,
        image,
        contents:
          kind === "feedback"
            ? []
            : form.contents.split(",").map((c) => c.trim()).filter(Boolean),
        creation_type: kind === "creation" ? form.creation_type : null,
        duration: kind === "challenge" ? form.duration : null,
        difficulty: kind === "challenge" ? form.difficulty : null,
        feedback_type: kind === "feedback" ? form.feedback_type : null,
      };
      const { data } = await api.post("/posts", payload);
      toast.success("Posted to the Forge.");
      navigate(`/community/${data.post_id}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="new-post-page" className="min-h-screen pt-28 pb-24">
      <div className="mx-auto w-[min(720px,calc(100%-32px))]">
        <div className="eyebrow-label mb-3">New post</div>
        <h1 className="headline text-4xl sm:text-5xl mb-10">
          <span className="text-white">What are you </span>
          <span className="text-forge">forging?</span>
        </h1>

        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { id: "creation", label: "Creation" },
            { id: "challenge", label: "Challenge" },
            { id: "feedback", label: "Feedback" },
          ].map((k) => (
            <button
              key={k.id}
              data-testid={`kind-${k.id}`}
              onClick={() => setKind(k.id)}
              className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border transition-colors ${
                kind === k.id ? "border-forge text-forge bg-forge/10" : "border-white/10 text-white/45 hover:text-white"
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid="post-title-input"
            className={field}
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />

          {kind === "creation" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <select
                data-testid="creation-type-select"
                className={field}
                value={form.creation_type}
                onChange={(e) => setForm({ ...form, creation_type: e.target.value })}
              >
                <option value="dice">A dice</option>
                <option value="pouch">A pouch</option>
              </select>
              <input
                data-testid="contents-input"
                className={field}
                placeholder={form.creation_type === "dice" ? "Faces, comma separated" : "Dice in the pouch, comma separated"}
                value={form.contents}
                onChange={(e) => setForm({ ...form, contents: e.target.value })}
              />
            </div>
          )}

          {kind === "challenge" && (
            <div className="grid sm:grid-cols-3 gap-4">
              <input
                data-testid="duration-input"
                className={field}
                placeholder="Duration (e.g. 30 days)"
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
              />
              <select
                data-testid="difficulty-select"
                className={field}
                value={form.difficulty}
                onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              >
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
              <input
                data-testid="contents-input"
                className={field}
                placeholder="Dice / Pouches used"
                value={form.contents}
                onChange={(e) => setForm({ ...form, contents: e.target.value })}
              />
            </div>
          )}

          {kind === "feedback" && (
            <select
              data-testid="feedback-type-select"
              className={field}
              value={form.feedback_type}
              onChange={(e) => setForm({ ...form, feedback_type: e.target.value })}
            >
              <option value="bug">Bug</option>
              <option value="idea">Idea</option>
              <option value="tweak">Tweak</option>
            </select>
          )}

          <textarea
            data-testid="post-body-input"
            className={`${field} min-h-[170px]`}
            placeholder={kind === "feedback" ? "Describe it as clearly as you can" : "Why does it work? How do you run it?"}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />

          {kind !== "feedback" && (
            <div>
              <div className="display text-[10px] tracking-[0.25em] text-white/40 mb-3">Tags</div>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map((t) => {
                  const active = tags.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      data-testid={`tag-${t}`}
                      onClick={() =>
                        setTags(active ? tags.filter((x) => x !== t) : [...tags, t].slice(0, 6))
                      }
                      className={`text-[11px] px-3 py-1.5 rounded-full border transition-colors ${
                        active ? "border-ember text-ember bg-ember/10" : "border-white/10 text-white/45 hover:text-white"
                      }`}
                    >
                      #{t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="display text-[10px] tracking-[0.25em] text-white/40 mb-3">Image (optional)</div>
            <input
              data-testid="post-image-input"
              type="file"
              accept="image/*"
              onChange={pickImage}
              className="text-xs text-white/50"
            />
            {image && <img src={image} alt="preview" className="mt-4 rounded-xl max-h-52 border border-white/10" />}
          </div>

          {error && (
            <p data-testid="post-error" className="text-sm text-forge">
              {error}
            </p>
          )}

          <button
            data-testid="post-submit-btn"
            disabled={busy}
            className="display text-sm tracking-[0.2em] px-8 py-4 rounded-full bg-forge text-white hover:bg-ember disabled:opacity-50 transition-colors"
          >
            {busy ? "Posting…" : "Post it"}
          </button>
        </form>
      </div>
    </main>
  );
}
